import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  GitBranchFromCurrentResponse,
  GitCommit,
  GitJumpResponse,
  GitPublishResponse,
  GitReturnResponse,
  GitSaveResponse,
  GitState,
} from '@claude-code-api/shared';
import type { ChatSessionMeta } from '@/components/talk/types';
import type { TalkSessionActions, TalkSessionDataState } from './types';

function getToastMethod(status: string) {
  if (status === 'error' || status === 'conflict_restore_failed') return toast.error;
  if (status === 'blocked_dirty_state' || status === 'blocked_missing_upstream' || status === 'travel_active') return toast.warning;
  if (status === 'no_op') return toast.info;
  return toast.success;
}

export function useTalkSessionData(sessionId: string): TalkSessionDataState & TalkSessionActions {
  const navigate = useNavigate();
  const [session, setSession] = useState<TalkSessionDataState['session']>(null);
  const [accessInfo, setAccessInfo] = useState<TalkSessionDataState['accessInfo']>(null);
  const [loading, setLoading] = useState(true);
  const [chatSessions, setChatSessions] = useState<TalkSessionDataState['chatSessions']>([]);
  const [activeChatSessionId, setActiveChatSessionId] = useState<TalkSessionDataState['activeChatSessionId']>(null);
  const [chatSessionsLoading, setChatSessionsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<TalkSessionDataState['actionLoading']>(null);
  const [chatRefreshKey, setChatRefreshKey] = useState(0);
  const [gitState, setGitState] = useState<GitState | null>(null);
  const [gitHistoryLoading, setGitHistoryLoading] = useState(true);
  const [gitHistoryError, setGitHistoryError] = useState<string | null>(null);

  const fetchSession = useCallback(async (keepLoading = false) => {
    try {
      const sessionResponse = await fetch(`/api/sessions/${sessionId}`, { credentials: 'include' });
      if (!sessionResponse.ok) {
        navigate('/talk', { replace: true });
        return;
      }

      const data = await sessionResponse.json();
      const nextSession = data.data;
      setSession(nextSession);

      if (nextSession.status === 'running') {
        const accessResponse = await fetch(`/api/sessions/${sessionId}/access`, { credentials: 'include' });
        if (accessResponse.ok) {
          const accessData = await accessResponse.json();
          setAccessInfo(accessData.data || null);
        }
      } else {
        setAccessInfo(null);
      }
    } catch (error) {
      console.error('Failed to fetch talk session:', error);
      toast.error('Failed to load Talk session');
    } finally {
      if (!keepLoading) setLoading(false);
    }
  }, [navigate, sessionId]);

  const fetchChatSessions = useCallback(async () => {
    try {
      const response = await fetch(`/api/acpx/sessions/${sessionId}/chat-sessions`, { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const sessions = (data?.sessions || []) as ChatSessionMeta[];
      setChatSessions(sessions);
      setActiveChatSessionId((data?.activeSessionId || sessions.find((item) => item.active)?.id || sessions[0]?.id || null) as string | null);
    } catch (error) {
      console.error('Failed to load PM chat sessions:', error);
      toast.error('Failed to load PM chat sessions');
    } finally {
      setChatSessionsLoading(false);
    }
  }, [sessionId]);

  const fetchGitHistory = useCallback(async () => {
    setGitHistoryLoading(true);
    try {
      const response = await fetch(`/api/acpx/sessions/${sessionId}/git-state`, { credentials: 'include' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setGitState((data || null) as GitState | null);
      setGitHistoryError(null);
    } catch (error) {
      console.error('Failed to load git state:', error);
      setGitHistoryError(error instanceof Error ? error.message : 'Failed to load git state');
    } finally {
      setGitHistoryLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void Promise.all([fetchSession(), fetchChatSessions(), fetchGitHistory()]);
  }, [fetchChatSessions, fetchGitHistory, fetchSession]);

  const bumpChatRefreshKey = useCallback(() => {
    setChatRefreshKey((value) => value + 1);
  }, []);

  const createChatSession = useCallback(async () => {
    setActionLoading('create');
    try {
      const response = await fetch(`/api/acpx/sessions/${sessionId}/chat-sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: 'New Session' }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await fetchChatSessions();
      bumpChatRefreshKey();
    } catch (error) {
      console.error('Failed to create chat session:', error);
      toast.error('Failed to create chat session');
    } finally {
      setActionLoading(null);
    }
  }, [bumpChatRefreshKey, fetchChatSessions, sessionId]);

  const activateChatSession = useCallback(async (chatSessionId: string) => {
    setActionLoading(`activate:${chatSessionId}`);
    try {
      const response = await fetch(`/api/acpx/sessions/${sessionId}/chat-sessions/${chatSessionId}/activate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setActiveChatSessionId(chatSessionId);
      await fetchChatSessions();
      bumpChatRefreshKey();
    } catch (error) {
      console.error('Failed to activate chat session:', error);
      toast.error('Failed to switch PM chat');
    } finally {
      setActionLoading(null);
    }
  }, [bumpChatRefreshKey, fetchChatSessions, sessionId]);

  const deleteChatSession = useCallback(async (chatSessionId: string) => {
    if (!window.confirm('Delete this PM chat session?')) return;
    setActionLoading(`delete:${chatSessionId}`);
    try {
      const response = await fetch(`/api/acpx/sessions/${sessionId}/chat-sessions/${chatSessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await fetchChatSessions();
      bumpChatRefreshKey();
    } catch (error) {
      console.error('Failed to delete chat session:', error);
      toast.error('Failed to delete PM chat');
    } finally {
      setActionLoading(null);
    }
  }, [bumpChatRefreshKey, fetchChatSessions, sessionId]);

  const bulkDeleteChatSessions = useCallback(async (chatSessionIds: string[]) => {
    const targets = Array.from(new Set(chatSessionIds.filter(Boolean)));
    if (targets.length === 0) return;
    if (!window.confirm(`Delete ${targets.length} PM chat session${targets.length > 1 ? 's' : ''}?`)) return;

    setActionLoading('bulk-delete');
    try {
      for (const chatSessionId of targets) {
        const response = await fetch(`/api/acpx/sessions/${sessionId}/chat-sessions/${chatSessionId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      }
      await fetchChatSessions();
      bumpChatRefreshKey();
      toast.success(`Deleted ${targets.length} thread${targets.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Failed to bulk delete chat sessions:', error);
      toast.error('Failed to delete selected threads');
    } finally {
      setActionLoading(null);
    }
  }, [bumpChatRefreshKey, fetchChatSessions, sessionId]);

  const commitRename = useCallback(async (chatSession: ChatSessionMeta, nextName: string) => {
    const trimmedName = nextName.trim();
    if (!trimmedName || trimmedName === chatSession.name) return;

    setActionLoading(`rename:${chatSession.id}`);
    try {
      const response = await fetch(`/api/acpx/sessions/${sessionId}/chat-sessions/${chatSession.id}/rename`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: trimmedName }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await fetchChatSessions();
    } catch (error) {
      console.error('Failed to rename PM chat session:', error);
      toast.error('Failed to rename PM chat');
    } finally {
      setActionLoading(null);
    }
  }, [fetchChatSessions, sessionId]);

  const runGitCommand = useCallback(async <T extends GitSaveResponse | GitPublishResponse | GitJumpResponse | GitReturnResponse | GitBranchFromCurrentResponse>(
    endpoint: string,
    loadingKey: string,
    body?: Record<string, unknown>,
  ) => {
    setActionLoading(loadingKey);
    try {
      const response = await fetch(`/api/acpx/sessions/${sessionId}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body || {}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(String(data?.error || `HTTP ${response.status}`));
      }

      const result = data as T;
      if (result?.state) setGitState(result.state);
      const notify = getToastMethod(String(result?.status || 'ok'));
      notify(String(result?.message || 'Git operation completed.'), {
        description: result?.commitMessage || result?.commitHash || result?.recoveryBranch || undefined,
      });
      return result;
    } catch (error) {
      console.error(`Failed to run git command ${endpoint}:`, error);
      toast.error(error instanceof Error ? error.message : 'Git operation failed');
      return null;
    } finally {
      setActionLoading(null);
    }
  }, [sessionId]);

  const runGitAction = useCallback(async (mode: 'save' | 'publish') => {
    await runGitCommand(mode === 'save' ? '/git/save' : '/git/publish', mode);
  }, [runGitCommand]);

  const jumpToCommit = useCallback(async (commit: GitCommit) => {
    await runGitCommand('/git/jump', `jump:${commit.hash}`, { targetCommitHash: commit.hash });
  }, [runGitCommand]);

  const jumpBack = useCallback(async () => {
    await runGitCommand('/git/return', 'return');
  }, [runGitCommand]);

  return {
    session,
    accessInfo,
    loading,
    chatSessions,
    activeChatSessionId,
    chatSessionsLoading,
    actionLoading,
    chatRefreshKey,
    gitState,
    gitHistoryLoading,
    gitHistoryError,
    fetchSession,
    fetchChatSessions,
    fetchGitHistory,
    createChatSession,
    activateChatSession,
    deleteChatSession,
    bulkDeleteChatSessions,
    commitRename,
    runGitAction,
    jumpToCommit,
    jumpBack,
    bumpChatRefreshKey,
  };
}
